// ABOUTME: SPEC-82 Rev 3 (bug 4) — the small privacy/consent panel opened from the collection menu, hung under the hamburger (top-left)
// ABOUTME: instead of in the bag rail, which collection_menu moved to the top-right. The SAME ConsentPanel, with each composition's geometry.

import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import ConsentPanel, { FIT_GUTTER_PX, captionBarTop } from '../../ReelChrome/ConsentPanel';
import { useViewportSize } from '../../Projectionist/useViewportSize';
import {
  u, CONSENT_PANEL, PANEL_W, PANEL_PAD, PANEL_RADIUS, GLYPHS, INDICATOR, RAIL_LEFT_PCT, panelWidth, railUnitFor,
} from '../../ReelChrome/railGeometry';
import {
  mu, MOBILE_PANEL, MOBILE_CONSENT, MOBILE_CONSENT_TEXT, mobileUnitFor, MENU_CHIP, MENU_GAP, WORDMARK, CONNECTION_INDICATOR_GAP,
} from '../../ReelChrome/mobileGeometry';

// Exactly what IconRail (desktop) and MobileChrome (mobile) pass their own ConsentPanel, so the panel looks the same; only its corner
// and its floor change.
// Desktop: the connection indicator sits centred under the wordmark (bag_top_right / collection_menu), level with this panel's top.
// On a narrow desktop window the rail's width would reach it (768x1024 render, 2026-10-06), so the width also stops short of the
// centre by the indicator's half-width; on every wide screen the rail's own width is the smaller term and nothing changes.
const DESKTOP_WIDTH = `min(${panelWidth()}, calc(50vw - ${RAIL_LEFT_PCT}vw - max(48px, 110 * var(--u))))`;

const PANEL_PROPS = {
  desktop: (vw) => ({
    unit: u, geometry: CONSENT_PANEL, panelW: PANEL_W, widthCss: DESKTOP_WIDTH, panelPad: PANEL_PAD, panelRadius: PANEL_RADIUS,
    baseUnitPx: railUnitFor(vw),
  }),
  mobile: (vw) => ({
    unit: mu, geometry: MOBILE_CONSENT, text: MOBILE_CONSENT_TEXT, panelW: MOBILE_PANEL.w, panelPad: MOBILE_PANEL.pad,
    panelRadius: MOBILE_PANEL.radius, baseUnitPx: mobileUnitFor(vw),
  }),
};

// Mobile: the panel is near full width, so under the hamburger it would cover the bags' chip stack (top-right) and the connection
// indicator under the wordmark (real-app render, 390x844, 2026-10-06). It starts below both instead; offsets are from the menu
// column's top (the hamburger's top), in the column's own mobile unit. The indicator's line keeps its 12px type floor (INDICATOR).
const BAGS_BOTTOM = MENU_CHIP.size * 2 + MENU_GAP;
const INDICATOR_TOP = WORDMARK.top + WORDMARK.h + CONNECTION_INDICATOR_GAP - MENU_CHIP.top;
const INDICATOR_LINE = `max(${(INDICATOR.font * 1.5).toFixed(3)} * var(--u), ${INDICATOR.minFontMobile * 1.5}px)`;
const MOBILE_PLACEMENT = {
  position: 'absolute', left: 0,
  top: `calc(max(${BAGS_BOTTOM} * var(--u), ${INDICATOR_TOP} * var(--u) + ${INDICATOR_LINE}) + ${MENU_GAP} * var(--u))`,
};

// Under the hamburger the panel shares its column with the bottom band: the CC bar, the PDP (pill + card) and the tag UI (owner
// 2026-10-06: it "must not overlap the PDP/spec sheet or the tag UI while open"), and the dock's controls row. An inert layer is the
// hidden sheet, not an obstacle.
const FLOOR_SELECTOR = [
  '[data-testid="reel-caption-bar"]', '[data-testid="spec-sheet-pill"]', '[data-testid="spec-sheet-card"]',
  '[data-testid="reel-tag-row"]', '[data-testid="reel-product-card"]',
  // The voice dock's revealed controls row (control_reveal): on mobile it is left-aligned, i.e. right under this panel's column.
  '[data-vf="row"]',
].join(', ');

// A piece shares the panel's columns only when it really sits under it. On an 844x390 landscape phone the centred controls row's
// keyboard button reaches ~15px under the panel's right edge; counting that sliver lifted the floor ~55px above the CC bar and the
// panel collapsed to its header, hiding every consent toggle (review 2026-10-06).
export const MIN_SHARED_COLUMN_PX = 24;

/**
 * The least height the panel is usable in, measured at its natural size: everything except the scrolling categories list, plus one
 * category row (header, link, a toggle, CANCEL/SAVE). The shortest row, not the first: "Strictly necessary" carries the longest
 * copy, and demanding it would give up the obstacle floor on a small phone that fits a shorter row and scrolls to the rest.
 * With no category row it is the whole content.
 * @param {Element} content the panel's content box
 * @returns {number} px
 */
export function minimumUsableHeight(content) {
  const rows = Array.from(content.querySelectorAll('label'));
  if (!rows.length) return content.scrollHeight;
  let list = rows[0];
  while (list.parentElement && list.parentElement !== content) list = list.parentElement;
  const shortestRow = Math.min(...rows.map((row) => row.getBoundingClientRect().height));
  return content.scrollHeight - list.getBoundingClientRect().height + shortestRow;
}

/**
 * The y the panel must stay above: the highest top among the visible bottom-band pieces below the panel's top that share its columns.
 * When that leaves less room than the panel's minimum usable height, the floor falls back to the CC bar (the floor every other
 * consent panel uses): a consent choice the guest cannot see or reach is worse than a panel over the band.
 * Pure DOM read, O(pieces) (a handful), run once per fit. Never throws; with nothing below it falls back to the viewport's bottom.
 * @param {Element} content the panel's content box
 * @returns {number} px from the viewport top
 */
export function obstacleFloorTop(content) {
  const panel = content.getBoundingClientRect();
  let floor = window.innerHeight;
  document.querySelectorAll(FLOOR_SELECTOR).forEach((el) => {
    if (el.closest('[inert]')) return;
    const r = el.getBoundingClientRect();
    const shared = Math.min(r.right, panel.right) - Math.max(r.left, panel.left);
    const sharesColumns = r.width > 0 && r.height > 0 && shared >= MIN_SHARED_COLUMN_PX;
    if (sharesColumns && r.top > panel.top && r.top < floor) floor = r.top;
  });
  if (floor - panel.top - FIT_GUTTER_PX < minimumUsableHeight(content)) return captionBarTop();
  return floor;
}

// How often an OPEN panel re-reads the bottom band. The PDP pill can open into its card, a chapter change brings the tag row, the
// controls row lifts the band — none of them tells this panel, so it looks (a handful of rect reads) and re-fits only on a change.
export const FLOOR_WATCH_MS = 400;

const obstacleSignature = () => Array.from(document.querySelectorAll(FLOOR_SELECTOR))
  .filter((el) => !el.closest('[inert]'))
  .map((el) => { const r = el.getBoundingClientRect(); return `${Math.round(r.top)}:${Math.round(r.left)}:${Math.round(r.width)}`; })
  .join('|');

/** A key that changes whenever the bottom band under the panel moves while it is open (ConsentPanel re-fits on a new key). */
export function useFloorRefitKey(open) {
  const [key, setKey] = useState(0);
  const lastRef = useRef('');
  useEffect(() => {
    if (!open) return undefined;
    lastRef.current = obstacleSignature();
    const id = setInterval(() => {
      const next = obstacleSignature();
      if (next !== lastRef.current) { lastRef.current = next; setKey((k) => k + 1); }
    }, FLOOR_WATCH_MS);
    return () => clearInterval(id);
  }, [open]);
  return String(key);
}

/**
 * @param {{ composition: 'desktop'|'mobile', privacy: ({ open: boolean, consent: object, onSave: Function, onCollapse: Function,
 *           onOpenPrivacyPolicy: Function }|null) }} props  `privacy` null = Privacy is not the menu's (nothing renders)
 */
export default function MenuConsentPanel({ composition, privacy }) {
  const { width } = useViewportSize();
  const refitKey = useFloorRefitKey(!!privacy?.open);
  if (!privacy) return null;
  return (
    <div data-vf="menu-consent" style={{ pointerEvents: 'auto', ...(composition === 'desktop' ? {} : MOBILE_PLACEMENT) }}>
      {/* initial={false}: a panel already open at mount (a static plate, a remount) is shown, not animated in from zero height. */}
      <AnimatePresence initial={false}>
        {privacy.open && (
          <ConsentPanel
            key="menu-privacy-panel"
            {...PANEL_PROPS[composition === 'desktop' ? 'desktop' : 'mobile'](width)}
            glyph={GLYPHS.privacy}
            consent={privacy.consent}
            onSave={(categories) => { privacy.onSave(categories); privacy.onCollapse(); }}
            onCollapse={privacy.onCollapse}
            onOpenPrivacyPolicy={privacy.onOpenPrivacyPolicy}
            floorTop={obstacleFloorTop}
            refitKey={refitKey}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
